import React, { useEffect, useRef, useState } from "react";
import Map, { Marker, type MapRef } from "react-map-gl/maplibre";
import "maplibre-gl/dist/maplibre-gl.css";
import { Box, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import bbox from "@turf/bbox";
import {
  TerraDraw,
  TerraDrawPointMode,
  TerraDrawLineStringMode,
  TerraDrawPolygonMode,
  TerraDrawRectangleMode,
  TerraDrawRenderMode,
  TerraDrawModeUndoRedo,
  TerraDrawUndoRedoKeyboardShortcuts,
  type GeoJSONStoreFeatures,
  type HexColor
} from "terra-draw";
import { TerraDrawMapLibreGLAdapter } from "terra-draw-maplibre-gl-adapter";
import DrawingTools from "./DrawingTools";
import MapControls from "./map-controls/MapControls";
import { useGeolocation } from "../../hooks/useGeolocation";
import { CmTableSpatialTypeRaw, normaliseSpatialType } from "../../services/cmTables";

export type DrawMode = "point" | "polygon" | "rectangle" | "line" | null;

interface CmTableDrawMapProps {
  spatialType: CmTableSpatialTypeRaw;
  /** Multi-geometry forms: the set of drawable kinds (point/linestring/polygon). Shows a tool per kind. */
  allowedTypes?: Array<"point" | "linestring" | "polygon">;
  initialViewState?: { longitude: number; latitude: number; zoom: number };
  externalGeometry?: GeoJSON.Geometry | GeoJSON.Feature | GeoJSON.FeatureCollection | null;
  onGeometryChange: (geometry: GeoJSON.Geometry | null) => void;
  /** Preview color (polygon/line) resolved from the selected styling `type` — recolors the drawn feature live. */
  previewColor?: string;
  /** Preview icon URL (point) resolved from the selected styling `type` — shown as a marker on the drawn point. */
  previewIcon?: string;
}

const EMPTY_VIEW_STATE = { longitude: 133.7751, latitude: -25.2744, zoom: 3.5 };

// Idle/display mode — no drawing interaction, just renders the confirmed feature.
const RENDER_MODE = "render";

// Maps our toolbar's DrawMode to Terra Draw's registered mode names.
const TERRA_DRAW_MODE: Record<Exclude<DrawMode, null>, string> = {
  point: "point",
  polygon: "polygon",
  rectangle: "rectangle",
  line: "linestring"
};

/**
 * Terra Draw operates on single-component geometries (Point, LineString, Polygon).
 * For Multi* geometries we expand into individual components so all rings/lines/points
 * are visible in the preview map.  On save the drawn single geometry replaces the
 * previous multi-geometry — acceptable for the single-component draw workflow (MP-2).
 * Full multi-component vertex editing is tracked in CM-FE160-24.
 */
function expandToComponents(geometry: GeoJSON.Geometry | any): Array<{ geometry: GeoJSON.Geometry; modeName: string }> {
  // Handle FeatureCollection — expand each feature's geometry
  if ((geometry as any).type === "FeatureCollection") {
    return ((geometry as any).features ?? []).flatMap((f: any) => (f?.geometry ? expandToComponents(f.geometry) : []));
  }
  // Handle Feature — unwrap the geometry
  if ((geometry as any).type === "Feature") {
    return (geometry as any).geometry ? expandToComponents((geometry as any).geometry) : [];
  }
  switch (geometry.type) {
    case "Point":
      return [{ geometry, modeName: "point" }];
    case "LineString":
      return [{ geometry, modeName: "linestring" }];
    case "Polygon":
      return [{ geometry, modeName: "polygon" }];
    case "MultiPoint":
      return (geometry as GeoJSON.MultiPoint).coordinates.map((c) => ({
        geometry: { type: "Point" as const, coordinates: c },
        modeName: "point"
      }));
    case "MultiLineString":
      return (geometry as GeoJSON.MultiLineString).coordinates.map((c) => ({
        geometry: { type: "LineString" as const, coordinates: c },
        modeName: "linestring"
      }));
    case "MultiPolygon":
      return (geometry as GeoJSON.MultiPolygon).coordinates.map((c) => ({
        geometry: { type: "Polygon" as const, coordinates: c },
        modeName: "polygon"
      }));
    default:
      return [];
  }
}

const toFeature = (geometry: GeoJSON.Geometry, modeName: string): GeoJSONStoreFeatures =>
  ({
    type: "Feature",
    geometry,
    properties: { mode: modeName }
  }) as unknown as GeoJSONStoreFeatures;

/** The base kind (point/linestring/polygon) of a geometry — drives toolbar locking + row spatial_type. */
export function kindOfGeometry(geom: GeoJSON.Geometry | null | undefined): "point" | "linestring" | "polygon" | null {
  const t = geom?.type;
  if (t === "Point" || t === "MultiPoint") return "point";
  if (t === "LineString" || t === "MultiLineString") return "linestring";
  if (t === "Polygon" || t === "MultiPolygon") return "polygon";
  return null;
}

const CmTableDrawMap: React.FC<CmTableDrawMapProps> = ({
  spatialType,
  allowedTypes,
  initialViewState,
  externalGeometry,
  onGeometryChange,
  previewColor,
  previewIcon
}) => {
  const theme = useTheme();
  // MUI theme colors are plain strings; Terra Draw's styling types require the `#xxxxxx` literal shape.
  const primary = theme.palette.primary.main as HexColor;
  const mapRef = useRef<MapRef | null>(null);
  const drawRef = useRef<TerraDraw | null>(null);

  const [activeMode, setActiveMode] = useState<DrawMode>(null);
  const [hasGeometry, setHasGeometry] = useState(false);
  // The kind of the currently-drawn geometry (point/linestring/polygon) — locks the
  // toolbar to that kind while a geometry exists (multi-geometry maps).
  const [drawnKind, setDrawnKind] = useState<"point" | "linestring" | "polygon" | null>(null);
  const [mapReady, setMapReady] = useState(false);
  // Coordinates of the drawn point (point tables only) — anchors the preview icon marker.
  const [pointCoords, setPointCoords] = useState<[number, number] | null>(null);

  // Prevents feedback loop: when geometry comes from a draw/edit event we set this
  // flag so the externalGeometry sync useEffect skips that render cycle.
  const skipSyncRef = useRef(false);

  const { userLocation, isTrackingLocation, isGettingLocation, handleLocationRequest } = useGeolocation(mapRef);

  const handleZoomIn = () => {
    const map = mapRef.current?.getMap();
    if (map) map.easeTo({ zoom: map.getZoom() + 1 });
  };

  const handleZoomOut = () => {
    const map = mapRef.current?.getMap();
    if (map) map.easeTo({ zoom: map.getZoom() - 1 });
  };

  // Auto-zoom to the initial geometry (edit mode) — fire only once
  const autoZoomedRef = useRef(false);

  // Keep the latest callback without re-creating the Terra Draw instance
  const onGeometryChangeRef = useRef(onGeometryChange);
  onGeometryChangeRef.current = onGeometryChange;

  // Terra Draw manages the canvas cursor per-mode (crosshair to draw, grab to drag a
  // vertex); this is only the fallback for the surrounding container.
  const cursorStyle = activeMode ? "crosshair" : "default";

  // ── Initialise Terra Draw once the map is ready ────────────────────────────
  useEffect(() => {
    if (!mapReady || drawRef.current) return;
    const map = mapRef.current?.getMap();
    if (!map) return;

    const baseSpatial = normaliseSpatialType(spatialType);
    const isMultiType = spatialType.toLowerCase().startsWith("multi");

    // A feature is only emittable once it forms a valid geometry. While the user is
    // mid-draw (e.g. a line with a single click) the feature is incomplete and skipped.
    const isValidFeature = (f: any): boolean => {
      const g = f.geometry;
      if (g.type === "Point") return Array.isArray(g.coordinates) && g.coordinates.length >= 2;
      if (g.type === "LineString") return Array.isArray(g.coordinates) && g.coordinates.length >= 2;
      if (g.type === "Polygon") return Array.isArray(g.coordinates?.[0]) && g.coordinates[0].length >= 4;
      return true;
    };

    // Collect all complete drawn features and emit a single combined geometry.
    // Display-only RENDER_MODE features are excluded, as are terra-draw's internal
    // helper features (coordinatePoint vertex dots, closingPoint, snappingPoint) —
    // they share the same mode name as the real geometry so mode alone won't filter them.
    const isInternalMarker = (f: any) =>
      f.properties.coordinatePoint === true || f.properties.closingPoint === true || f.properties.snappingPoint === true;

    const emitCombined = (draw: TerraDraw) => {
      const drawn = draw.getSnapshot().filter((f: any) => f.properties.mode !== RENDER_MODE && !isInternalMarker(f) && isValidFeature(f));
      if (drawn.length === 0) {
        onGeometryChangeRef.current(null);
        setHasGeometry(false);
        setDrawnKind(null);
        return;
      }
      setHasGeometry(true);
      setDrawnKind(kindOfGeometry(drawn[drawn.length - 1].geometry as GeoJSON.Geometry));

      const lineStrings = drawn.filter((f: any) => f.geometry.type === "LineString");
      const polygons = drawn.filter((f: any) => f.geometry.type === "Polygon");
      const points = drawn.filter((f: any) => f.geometry.type === "Point");

      // Single-geometry columns hold exactly one feature — emit it as-is.
      if (!isMultiType) {
        onGeometryChangeRef.current(drawn[drawn.length - 1].geometry as GeoJSON.Geometry);
        return;
      }

      // Multi-geometry columns — combine same-typed features into a Multi* geometry.
      if (lineStrings.length > 0) {
        onGeometryChangeRef.current({
          type: "MultiLineString",
          coordinates: lineStrings.map((f: any) => (f.geometry as GeoJSON.LineString).coordinates)
        });
      } else if (polygons.length > 0) {
        onGeometryChangeRef.current({
          type: "MultiPolygon",
          coordinates: polygons.map((f: any) => (f.geometry as GeoJSON.Polygon).coordinates)
        });
      } else if (points.length > 0) {
        onGeometryChangeRef.current({
          type: "MultiPoint",
          coordinates: points.map((f: any) => (f.geometry as GeoJSON.Point).coordinates)
        });
      } else {
        onGeometryChangeRef.current(drawn[drawn.length - 1].geometry as GeoJSON.Geometry);
      }
    };

    const draw = new TerraDraw({
      adapter: new TerraDrawMapLibreGLAdapter({ map }),
      modes: [
        new TerraDrawPointMode({
          editable: true,
          styles: { pointColor: primary, pointOutlineColor: "#fff", pointOutlineWidth: 2, pointWidth: 7 }
        }),
        new TerraDrawLineStringMode({
          editable: true,
          showCoordinatePoints: true,
          pointerDistance: 12,
          styles: {
            lineStringColor: primary,
            lineStringWidth: 2,
            coordinatePointColor: primary,
            coordinatePointOutlineColor: "#ffffff" as HexColor,
            coordinatePointOutlineWidth: 2,
            coordinatePointWidth: 5
          }
        }),
        new TerraDrawPolygonMode({
          editable: true,
          showCoordinatePoints: true,
          pointerDistance: 12,
          styles: {
            fillColor: primary,
            fillOpacity: 0.25,
            outlineColor: primary,
            outlineWidth: 2,
            coordinatePointColor: primary,
            coordinatePointOutlineColor: "#ffffff" as HexColor,
            coordinatePointOutlineWidth: 2,
            coordinatePointWidth: 5
          }
        }),
        new TerraDrawRectangleMode({ styles: { fillColor: primary, fillOpacity: 0.25, outlineColor: primary, outlineWidth: 2 } }),
        new TerraDrawRenderMode({ modeName: RENDER_MODE, styles: {} })
      ],
      undoRedo: {
        modeLevel: new TerraDrawModeUndoRedo(),
        keyboardShortcuts: new TerraDrawUndoRedoKeyboardShortcuts()
      }
    });

    // The `finish` event fires after every completed interaction — both drawing a new
    // feature (action "draw") and editing an existing one (dragCoordinate, insertMidpoint,
    // deleteCoordinate, dragFeature). Because modes are `editable`, all features stay
    // editable in place with no select/deselect step.
    draw.on("finish", (id, context) => {
      const feature = draw.getSnapshotFeature(id);

      if (context.action === "draw") {
        // Single-geometry columns hold one feature — drop any earlier ones.
        // Exclude internal terra-draw markers — terra-draw owns their lifecycle;
        // deleting them here causes "can not delete" crashes mid-transaction.
        if (!isMultiType) {
          const others = draw.getSnapshot().filter((f: any) => f.id !== id && f.properties.mode !== RENDER_MODE && !isInternalMarker(f));
          if (others.length > 0) draw.removeFeatures(others.map((f: any) => f.id as string));
        }
        // Rectangles are drawn in their own mode — re-tag as polygon so their
        // vertices remain editable in the always-on polygon mode.
        if (feature && feature.properties.mode === "rectangle") {
          draw.removeFeatures([id]);
          draw.addFeatures([toFeature(feature.geometry as GeoJSON.Geometry, "polygon")]);
        }
        // Return to the primary editable mode so the new feature is immediately editable.
        setActiveMode(null);
      }

      skipSyncRef.current = true;
      emitCombined(draw);

      // Keep the preview-icon anchor in sync with the drawn point (point tables only).
      if (baseSpatial === "point") {
        const pts = draw
          .getSnapshot()
          .filter((f: any) => f.geometry.type === "Point" && f.properties.mode !== RENDER_MODE && !isInternalMarker(f));
        setPointCoords(pts.length ? ((pts[pts.length - 1].geometry as GeoJSON.Point).coordinates as [number, number]) : null);
      }
    });

    draw.start();

    if (externalGeometry) {
      const allComponents = expandToComponents(externalGeometry);
      // For single-geometry columns load only the first component so we never
      // seed the canvas with more than one feature (e.g. a saved FeatureCollection).
      const components = isMultiType ? allComponents : allComponents.slice(0, 1);
      try {
        for (const { geometry, modeName } of components) {
          draw.addFeatures([toFeature(geometry, modeName)]);
        }
        if (components.length > 0) {
          setHasGeometry(true);
          setDrawnKind(kindOfGeometry(components[0].geometry));
        }
        // Seed the preview-icon anchor from the initial point geometry (edit mode).
        if (baseSpatial === "point") {
          const firstPoint = components.find((c) => c.geometry.type === "Point");
          if (firstPoint) setPointCoords((firstPoint.geometry as GeoJSON.Point).coordinates as [number, number]);
        }
      } catch (e) {
        console.warn("[CmTableDrawMap] addFeatures failed during init:", e);
      }
    }

    // Start in the primary editable mode so all features are immediately editable.
    draw.setMode(
      baseSpatial === "point" ? "point" : baseSpatial === "linestring" ? "linestring" : baseSpatial === "polygon" ? "polygon" : RENDER_MODE
    );
    drawRef.current = draw;

    return () => {
      draw.stop();
      drawRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapReady]);

  // ── Drive Terra Draw's active mode from our toolbar state ──────────────────
  // Idle (no active draw tool) → the primary editable drawing mode for the spatial
  // type, so every feature's vertices are always visible and draggable. Picking a
  // specific tool (e.g. rectangle) temporarily switches to that mode.
  useEffect(() => {
    const draw = drawRef.current;
    if (!draw) return;
    if (activeMode) {
      draw.setMode(TERRA_DRAW_MODE[activeMode]);
      return;
    }
    const b = normaliseSpatialType(spatialType);
    const primaryMode = b === "point" ? "point" : b === "linestring" ? "linestring" : b === "polygon" ? "polygon" : RENDER_MODE;
    draw.setMode(primaryMode);
  }, [activeMode, spatialType]);

  // ── Live-recolor the drawn feature to preview the selected styling `type` ──
  // Point tables preview via an icon marker (see render); polygon/line tables
  // recolor Terra Draw's fill/outline/line without re-initialising the instance.
  useEffect(() => {
    const draw = drawRef.current;
    if (!draw || !previewColor) return;
    const b = normaliseSpatialType(spatialType);
    try {
      if (b === "polygon") {
        const styles = {
          fillColor: previewColor as HexColor,
          fillOpacity: 0.35,
          outlineColor: previewColor as HexColor,
          outlineWidth: 2,
          coordinatePointColor: previewColor as HexColor,
          coordinatePointOutlineColor: "#ffffff" as HexColor,
          coordinatePointOutlineWidth: 2,
          coordinatePointWidth: 5
        };
        (draw as any).updateModeOptions("polygon", { styles });
        (draw as any).updateModeOptions("rectangle", {
          styles: { fillColor: previewColor as HexColor, fillOpacity: 0.35, outlineColor: previewColor as HexColor, outlineWidth: 2 }
        });
      } else if (b === "linestring") {
        (draw as any).updateModeOptions("linestring", {
          styles: {
            lineStringColor: previewColor as HexColor,
            lineStringWidth: 2,
            coordinatePointColor: previewColor as HexColor,
            coordinatePointOutlineColor: "#ffffff" as HexColor,
            coordinatePointOutlineWidth: 2,
            coordinatePointWidth: 5
          }
        });
      }
    } catch (e) {
      console.warn("[CmTableDrawMap] preview color update failed:", e);
    }
  }, [previewColor, spatialType, mapReady, hasGeometry]);

  // ── Sync external geometry to map (Advanced GeoJSON Editor) ────────────────
  useEffect(() => {
    // Skip this cycle if the geometry change originated from our own drawing/editing —
    // prevents a feedback loop where emitting combined geometry re-triggers a clear.
    if (skipSyncRef.current) {
      skipSyncRef.current = false;
      return;
    }
    const draw = drawRef.current;
    if (!draw || !externalGeometry) return;

    setActiveMode(null);
    draw.clear();

    const allComponents = expandToComponents(externalGeometry);
    // For single-geometry columns limit to one component so pasting a
    // FeatureCollection into the Advanced GeoJSON Editor doesn't accumulate.
    const isMulti = spatialType.toLowerCase().startsWith("multi");
    const components = isMulti ? allComponents : allComponents.slice(0, 1);
    try {
      for (const { geometry, modeName } of components) {
        draw.addFeatures([toFeature(geometry, modeName)]);
      }
      if (components.length > 0) setHasGeometry(true);
      // Keep the preview-icon anchor aligned with the synced point geometry.
      if (normaliseSpatialType(spatialType) === "point") {
        const firstPoint = components.find((c) => c.geometry.type === "Point");
        setPointCoords(firstPoint ? ((firstPoint.geometry as GeoJSON.Point).coordinates as [number, number]) : null);
      }
    } catch (e) {
      console.warn("[CmTableDrawMap] addFeatures failed during sync:", e);
    }
    // Return to the primary editable mode so synced features stay editable in place.
    const b = normaliseSpatialType(spatialType);
    draw.setMode(b === "point" ? "point" : b === "linestring" ? "linestring" : b === "polygon" ? "polygon" : RENDER_MODE);
    // Don't call onGeometryChange here — it came FROM the parent already
  }, [externalGeometry, spatialType]);

  // ── Auto-zoom to initial geometry once the map is ready (edit mode) ────────
  useEffect(() => {
    if (!mapReady || !externalGeometry || autoZoomedRef.current) return;
    autoZoomedRef.current = true;
    const map = mapRef.current?.getMap();
    if (!map) return;
    try {
      if (externalGeometry.type === "Point") {
        const [lng, lat] = (externalGeometry as GeoJSON.Point).coordinates;
        map.flyTo({ center: [lng, lat], zoom: 14, duration: 500 });
      } else {
        const [minLng, minLat, maxLng, maxLat] = bbox(externalGeometry);
        if (isFinite(minLng)) {
          map.fitBounds(
            [
              [minLng, minLat],
              [maxLng, maxLat]
            ],
            { padding: 60, maxZoom: 15, duration: 500 }
          );
        }
      }
    } catch (_) {}
  }, [mapReady, externalGeometry]);

  // ── Toolbar handlers ────────────────────────────────────────────────────────
  const handleModeChange = (mode: DrawMode) => {
    // Toggle: clicking the active tool deactivates it; clicking a different tool switches.
    // Features are NOT cleared on mode switch — use Reset to start over.
    setActiveMode((prev) => (prev === mode ? null : mode));
  };

  const handleReset = () => {
    setActiveMode(null);
    setHasGeometry(false);
    setDrawnKind(null);
    setPointCoords(null);
    drawRef.current?.clear();
    onGeometryChange(null);
  };

  const baseSpatialType = normaliseSpatialType(spatialType);
  const isMultiType = spatialType.toLowerCase().startsWith("multi");
  const hintText =
    baseSpatialType === "point"
      ? "Click the map to place a point · Drag it to reposition"
      : baseSpatialType === "linestring"
        ? isMultiType
          ? "Click to add points · Double-click to finish · Draw again to add more lines · Drag points to edit · Click a line to add a point"
          : "Click to add points · Double-click to finish · Drag points to edit · Click the line to add a point"
        : isMultiType
          ? "Click to add points · Double-click to close · Draw again to add more shapes · Drag points to edit · Click an edge to add a point"
          : "Click to add points · Double-click to close · Drag points to edit · Click an edge to add a point";

  return (
    <Box sx={{ position: "relative", width: "100%", height: "100%", cursor: cursorStyle }}>
      {/* Compact hint bar */}
      {!hasGeometry && !externalGeometry && (
        <Box
          sx={{
            position: "absolute",
            top: "0.5rem",
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 10,
            bgcolor: "rgba(0,0,0,0.6)",
            color: "white",
            borderRadius: "0.5rem",
            px: "0.75rem",
            py: "0.3rem",
            pointerEvents: "none",
            whiteSpace: "nowrap"
          }}
        >
          <Typography variant='caption' sx={{ fontSize: "0.72rem" }}>
            {hintText}
          </Typography>
        </Box>
      )}

      <Map
        ref={mapRef}
        style={{ width: "100%", height: "100%" }}
        initialViewState={initialViewState ?? EMPTY_VIEW_STATE}
        mapStyle={
          process.env.REACT_APP_MAPTILER_KEY
            ? `https://api.maptiler.com/maps/hybrid/style.json?key=${process.env.REACT_APP_MAPTILER_KEY}`
            : "https://demotiles.maplibre.org/style.json"
        }
        attributionControl={false}
        onLoad={() => setMapReady(true)}
      >
        {userLocation && (
          <Marker longitude={userLocation.longitude} latitude={userLocation.latitude} anchor='center'>
            <Box
              sx={{
                width: 16,
                height: 16,
                borderRadius: "50%",
                backgroundColor: "#00bfff",
                border: "2px solid white",
                boxShadow: "0 2px 8px rgba(0,191,255,0.4)",
                animation: "bluePulse 2s ease-in-out infinite",
                "@keyframes bluePulse": {
                  "0%": { transform: "scale(1)", opacity: 1 },
                  "50%": { transform: "scale(1.1)", opacity: 0.8 },
                  "100%": { transform: "scale(1)", opacity: 1 }
                }
              }}
            />
          </Marker>
        )}

        {/* Preview icon for the selected styling `type` (point tables) */}
        {previewIcon && baseSpatialType === "point" && pointCoords && (
          <Marker longitude={pointCoords[0]} latitude={pointCoords[1]} anchor='bottom'>
            <Box
              component='img'
              src={previewIcon}
              alt=''
              sx={{ width: 34, height: 34, objectFit: "contain", pointerEvents: "none", filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.4))" }}
            />
          </Marker>
        )}
      </Map>

      {/* Drawing toolbar — bottom center */}
      <Box
        sx={{
          position: "absolute",
          bottom: "0.75rem",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 10
        }}
      >
        <DrawingTools
          spatialType={spatialType}
          allowedTypes={allowedTypes}
          activeMode={activeMode}
          onModeChange={handleModeChange}
          onReset={handleReset}
          lockedKind={(allowedTypes?.length ?? 0) > 1 ? drawnKind : null}
        />
      </Box>

      {/* Navigation controls — bottom right */}
      <Box sx={{ position: "absolute", bottom: "0.75rem", right: "0.75rem", zIndex: 10 }}>
        <MapControls
          onZoomIn={handleZoomIn}
          onZoomOut={handleZoomOut}
          onLocationRequest={handleLocationRequest}
          isTrackingLocation={isTrackingLocation}
          isGettingLocation={isGettingLocation}
        />
      </Box>
    </Box>
  );
};

export default CmTableDrawMap;
